import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation as useRouterLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../HomeScreen/Navbar';
import Footer from '../HomeScreen/bottomsection/Footer';
import Loader from '../../layout/loader';
import MetaTag from '../../layout/MetaTag';
import { useLocation } from '../../context/LocationContext';
import ShopCard from './ShopCard';
import MarketplaceProductCard from './MarketplaceProductCard';
import { getCategoryIcon } from './CategoryIcons';
import { recordCategoryInteraction } from '../../utils/userPreferences';
import './marketplaceSearch.css';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CloseIcon from '@mui/icons-material/Close';

const PRICE_RANGES = [
    { label: 'All Prices', min: 0, max: 999999 },
    { label: 'Under ₹500', min: 0, max: 500 },
    { label: '₹500 - ₹1,500', min: 500, max: 1500 },
    { label: '₹1,500 - ₹3,000', min: 1500, max: 3000 },
    { label: '₹3,000 - ₹5,000', min: 3000, max: 5000 },
    { label: '₹5,000+', min: 5000, max: 999999 }
];

function MarketplaceSearch() {
    const history = useHistory();
    const routerLocation = useRouterLocation();
    const { location, setIsLocationModalOpen } = useLocation();

    // Parse URL query params
    const searchParams = useMemo(() => new URLSearchParams(routerLocation.search), [routerLocation.search]);
    const initialQuery = searchParams.get('q') || searchParams.get('search') || searchParams.get('keyword') || '';
    const initialCategory = searchParams.get('category') || 'All';
    const initialSubcategory = searchParams.get('subcategory') || 'All';
    const initialMode = searchParams.get('type') || 'shops'; // Default to shops when type=shops
    const initialRadius = searchParams.get('radius') ? (searchParams.get('radius') === 'all' ? 'all' : Number(searchParams.get('radius'))) : (location.radius || 10);
    const initialPriceIdx = searchParams.get('price') ? parseInt(searchParams.get('price'), 10) : 0;
    const initialRating = searchParams.get('rating') ? parseFloat(searchParams.get('rating')) : 0;
    const initialBrand = searchParams.get('brand') || 'All';
    const initialSort = searchParams.get('sortBy') || 'distance';

    // State
    const [searchTerm, setSearchTerm] = useState(initialQuery);
    const [activeMode, setActiveMode] = useState(initialMode);
    const [selectedRadius, setSelectedRadius] = useState(initialRadius);
    const [selectedCategory, setSelectedCategory] = useState(initialCategory);
    const [selectedSubcategory, setSelectedSubcategory] = useState(initialSubcategory);
    const [selectedPriceIdx, setSelectedPriceIdx] = useState(initialPriceIdx);
    const [minRating, setMinRating] = useState(initialRating);
    const [selectedBrand, setSelectedBrand] = useState(initialBrand);
    const [sortBy, setSortBy] = useState(initialSort);

    // Dynamic Categories & Subcategories from Backend
    const [categories, setCategories] = useState([]);
    const [categoryDetails, setCategoryDetails] = useState({});
    const [availableBrands, setAvailableBrands] = useState([]);

    // Data results
    const [shops, setShops] = useState([]);
    const [products, setProducts] = useState([]);
    const [totalShops, setTotalShops] = useState(0);
    const [totalProducts, setTotalProducts] = useState(0);
    const [autoExpanded, setAutoExpanded] = useState(false);
    const [activeRadius, setActiveRadius] = useState(initialRadius);
    const [loading, setLoading] = useState(true);

    // Synchronize state from URL when URL changes
    useEffect(() => {
        const q = searchParams.get('q') || searchParams.get('search') || searchParams.get('keyword') || '';
        const cat = searchParams.get('category') || 'All';
        const sub = searchParams.get('subcategory') || 'All';
        const typ = searchParams.get('type') || 'shops';
        const radParam = searchParams.get('radius');
        let rad = location.radius || 'auto';
        if (radParam) {
            if (radParam === 'all') rad = 'all';
            else if (radParam === 'auto') rad = 'auto';
            else {
                const parsed = Number(radParam);
                rad = isNaN(parsed) ? 'auto' : parsed;
            }
        }
        const pIdx = searchParams.get('price') ? parseInt(searchParams.get('price'), 10) : 0;
        const rat = searchParams.get('rating') ? parseFloat(searchParams.get('rating')) : 0;
        const brd = searchParams.get('brand') || 'All';
        const srt = searchParams.get('sortBy') || 'distance';

        setSearchTerm(q);
        setSelectedCategory(cat);
        setSelectedSubcategory(sub);
        setActiveMode(typ);
        setSelectedRadius(rad);
        setSelectedPriceIdx(pIdx);
        setMinRating(rat);
        setSelectedBrand(brd);
        setSortBy(srt);
    }, [searchParams, location.radius]);

    // Helper to update URL params
    const updateUrlParams = useCallback((overrides = {}) => {
        const params = new URLSearchParams();
        const q = overrides.searchTerm !== undefined ? overrides.searchTerm : searchTerm;
        const typ = overrides.activeMode !== undefined ? overrides.activeMode : activeMode;
        const cat = overrides.selectedCategory !== undefined ? overrides.selectedCategory : selectedCategory;
        const sub = overrides.selectedSubcategory !== undefined ? overrides.selectedSubcategory : selectedSubcategory;
        const rad = overrides.selectedRadius !== undefined ? overrides.selectedRadius : selectedRadius;
        const pIdx = overrides.selectedPriceIdx !== undefined ? overrides.selectedPriceIdx : selectedPriceIdx;
        const rat = overrides.minRating !== undefined ? overrides.minRating : minRating;
        const brd = overrides.selectedBrand !== undefined ? overrides.selectedBrand : selectedBrand;
        const srt = overrides.sortBy !== undefined ? overrides.sortBy : sortBy;

        if (q && q.trim()) params.set('q', q.trim());
        if (typ) params.set('type', typ);
        if (cat && cat !== 'All') params.set('category', cat);
        if (sub && sub !== 'All') params.set('subcategory', sub);
        if (rad && rad !== 10) params.set('radius', rad.toString());
        if (pIdx > 0) params.set('price', pIdx.toString());
        if (rat > 0) params.set('rating', rat.toString());
        if (brd && brd !== 'All') params.set('brand', brd);
        if (srt && srt !== 'distance') params.set('sortBy', srt);

        const newSearch = params.toString();
        history.replace(`/marketplace/search${newSearch ? `?${newSearch}` : ''}`);
    }, [history, searchTerm, activeMode, selectedCategory, selectedSubcategory, selectedRadius, selectedPriceIdx, minRating, selectedBrand, sortBy]);

    // 1. Fetch Dynamic Category Taxonomy from Backend
    useEffect(() => {
        let isMounted = true;
        const fetchCategories = async () => {
            try {
                const res = await axios.get('/api/marketplace/categories');
                if (isMounted && res.data && res.data.categories) {
                    setCategories(res.data.categories);
                }
            } catch (err) {
                console.error("Failed to load marketplace categories:", err);
            }
        };
        fetchCategories();
        return () => { isMounted = false; };
    }, []);

    // 2. Fetch Subcategories when Category Changes
    useEffect(() => {
        if (!selectedCategory || selectedCategory === 'All') return;

        const activeCatObj = categories.find(
            c => c.name?.toLowerCase() === selectedCategory.toLowerCase() ||
                 c.id?.toLowerCase() === selectedCategory.toLowerCase() ||
                 c._id?.toString() === selectedCategory
        );

        if (activeCatObj) {
            const catId = activeCatObj._id || activeCatObj.id;
            if (!categoryDetails[catId]) {
                axios.get(`/api/categories/${catId}`)
                    .then(res => {
                        if (res.data?.category) {
                            setCategoryDetails(prev => ({
                                ...prev,
                                [catId]: res.data.category
                            }));
                        }
                    })
                    .catch(() => {});
            }
        }
    }, [selectedCategory, categories, categoryDetails]);

    // 3. Fetch Search & Filter Results
    useEffect(() => {
        let isCancelled = false;
        const fetchResults = async () => {
            try {
                setLoading(true);
                const activePrice = PRICE_RANGES[selectedPriceIdx] || PRICE_RANGES[0];
                const geoParts = [];
                if (location.lat != null && !isNaN(location.lat)) geoParts.push(`lat=${location.lat}`);
                if (location.lng != null && !isNaN(location.lng)) geoParts.push(`lng=${location.lng}`);
                if (location.city && location.city !== 'All Locations') geoParts.push(`city=${encodeURIComponent(location.city)}`);
                if (location.pincode) geoParts.push(`pincode=${encodeURIComponent(location.pincode)}`);
                const geoStr = geoParts.length > 0 ? `${geoParts.join('&')}&` : '';
                const manualParam = (selectedRadius !== 'auto' && selectedRadius !== 'all') ? '&manual=true' : '';

                let shopUrl = `/api/marketplace/shops?${geoStr}radius=${selectedRadius}${manualParam}&search=${encodeURIComponent(searchTerm)}&sortBy=${sortBy}`;
                if (selectedCategory && selectedCategory !== 'All') {
                    shopUrl += `&category=${encodeURIComponent(selectedCategory)}`;
                }

                let prodUrl = `/api/marketplace/products?${geoStr}radius=${selectedRadius}${manualParam}&keyword=${encodeURIComponent(searchTerm)}&sortBy=${sortBy}&minPrice=${activePrice.min}&maxPrice=${activePrice.max}`;
                if (selectedCategory && selectedCategory !== 'All') {
                    prodUrl += `&category=${encodeURIComponent(selectedCategory)}`;
                }
                if (selectedSubcategory && selectedSubcategory !== 'All') {
                    prodUrl += `&subcategory=${encodeURIComponent(selectedSubcategory)}`;
                }
                if (selectedBrand && selectedBrand !== 'All') {
                    prodUrl += `&brand=${encodeURIComponent(selectedBrand)}`;
                }
                if (minRating > 0) {
                    prodUrl += `&minRating=${minRating}`;
                }

                const [shopRes, prodRes] = await Promise.all([
                    axios.get(shopUrl),
                    axios.get(prodUrl)
                ]);

                if (isCancelled) return;

                setShops(shopRes.data.shops || []);
                setTotalShops(shopRes.data.totalShops || 0);
                setAutoExpanded(shopRes.data.autoExpanded || prodRes.data.autoExpanded || false);
                setActiveRadius(shopRes.data.activeRadius || selectedRadius);

                const prods = prodRes.data.products || [];
                setProducts(prods);
                setTotalProducts(prodRes.data.totalProducts || 0);

                const brands = Array.from(new Set(prods.map(p => p.brandName).filter(Boolean)));
                setAvailableBrands(brands);

                if (searchTerm) recordCategoryInteraction(searchTerm, 0.5);
                if (selectedCategory && selectedCategory !== 'All') recordCategoryInteraction(selectedCategory, 1);
            } catch (err) {
                if (!isCancelled) {
                    console.error("Marketplace search error:", err);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        const timer = setTimeout(() => {
            fetchResults();
        }, 150);

        return () => {
            isCancelled = true;
            clearTimeout(timer);
        };
    }, [
        searchTerm,
        selectedRadius,
        selectedCategory,
        selectedSubcategory,
        selectedPriceIdx,
        minRating,
        selectedBrand,
        sortBy,
        location.lat,
        location.lng,
        location.city,
        location.pincode
    ]);

    // Handlers
    const handleSearchSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        updateUrlParams({ searchTerm });
    };

    const handleCategorySelect = (catName) => {
        setSelectedCategory(catName);
        setSelectedSubcategory('All');
        setSelectedBrand('All');
        updateUrlParams({ selectedCategory: catName, selectedSubcategory: 'All', selectedBrand: 'All' });
    };

    const handleSubcategorySelect = (subName) => {
        setSelectedSubcategory(subName);
        updateUrlParams({ selectedSubcategory: subName });
    };

    const handleModeChange = (mode) => {
        setActiveMode(mode);
        updateUrlParams({ activeMode: mode });
    };

    const handleRadiusChange = (rad) => {
        setSelectedRadius(rad);
        updateUrlParams({ selectedRadius: rad });
    };

    const handleSortChange = (newSort) => {
        setSortBy(newSort);
        updateUrlParams({ sortBy: newSort });
    };

    const handlePriceChange = (idx) => {
        setSelectedPriceIdx(idx);
        updateUrlParams({ selectedPriceIdx: idx });
    };

    const handleRatingChange = (rat) => {
        setMinRating(rat);
        updateUrlParams({ minRating: rat });
    };

    const handleBrandChange = (brd) => {
        setSelectedBrand(brd);
        updateUrlParams({ selectedBrand: brd });
    };

    const handleClearAllFilters = () => {
        setSelectedCategory('All');
        setSelectedSubcategory('All');
        setSelectedBrand('All');
        setSelectedPriceIdx(0);
        setMinRating(0);
        setSelectedRadius('all');
        updateUrlParams({
            selectedCategory: 'All',
            selectedSubcategory: 'All',
            selectedBrand: 'All',
            selectedPriceIdx: 0,
            minRating: 0,
            selectedRadius: 'all'
        });
    };

    const activeCatObj = categories.find(
        c => c.name?.toLowerCase() === selectedCategory.toLowerCase() ||
             c.id?.toLowerCase() === selectedCategory.toLowerCase()
    );
    const activeCatDetails = activeCatObj ? categoryDetails[activeCatObj._id || activeCatObj.id] : null;
    const subcategoryList = activeCatDetails?.children || activeCatDetails?.subcategories || [];

    const isFiltered = selectedCategory !== 'All' || selectedSubcategory !== 'All' || selectedBrand !== 'All' || selectedPriceIdx > 0 || minRating > 0 || selectedRadius !== 10;

    return (
        <div className="ms-page">
            <MetaTag
                title={`${searchTerm ? `Search "${searchTerm}"` : activeMode === 'shops' ? 'Explore Nearby Shops' : selectedCategory !== 'All' ? selectedCategory : 'Local Marketplace'} | IM Online`}
                description="Discover and connect with verified local retail shops, boutiques, and sellers in your city."
            />
            {/* Global Navbar */}
            <Navbar />

            {/* ═════════════════ STICKY SEARCH & CONTROLS DOCK ═════════════════ */}
            <div className="ms-sticky-header">
                <div className="ms-container">
                    {/* Top Row: Search Input + Location Pill */}
                    <div className="ms-search-top-row">
                        <form className="ms-search-form" onSubmit={handleSearchSubmit}>
                            <SearchIcon className="ms-search-icon" />
                            <input
                                type="text"
                                className="ms-search-input"
                                placeholder={activeMode === 'shops' ? "Search nearby shops, boutiques, store names..." : "Search products, brands, footwear, groceries..."}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="ms-search-clear-btn"
                                    onClick={() => {
                                        setSearchTerm('');
                                        updateUrlParams({ searchTerm: '' });
                                    }}
                                    aria-label="Clear search"
                                >
                                    <CloseIcon />
                                </button>
                            )}
                        </form>

                        <button
                            type="button"
                            className="ms-location-pill"
                            onClick={() => setIsLocationModalOpen(true)}
                            title="Click to change your delivery location or radius"
                        >
                            <LocationOnIcon />
                            <span>{location.label || 'Select Location'}</span>
                            <span className="ms-change-text">[Change]</span>
                        </button>
                    </div>

                    {/* Controls Bar: Mode Switcher Tabs + Filters Rail */}
                    <div className="ms-controls-bar">
                        {/* Mode Switcher */}
                        <div className="ms-mode-switch">
                            <button
                                type="button"
                                className={`ms-mode-btn ${activeMode === 'shops' ? 'active' : ''}`}
                                onClick={() => handleModeChange('shops')}
                            >
                                <StorefrontIcon />
                                <span>Nearby Shops ({totalShops})</span>
                            </button>
                            <button
                                type="button"
                                className={`ms-mode-btn ${activeMode === 'products' ? 'active' : ''}`}
                                onClick={() => handleModeChange('products')}
                            >
                                <Inventory2Icon />
                                <span>Products ({totalProducts})</span>
                            </button>
                        </div>

                        {/* Filters Rail */}
                        <div className="ms-filters-rail">
                            {/* Category Selector */}
                            <select
                                className={`ms-filter-select ${selectedCategory !== 'All' ? 'active' : ''}`}
                                value={selectedCategory}
                                onChange={(e) => handleCategorySelect(e.target.value)}
                            >
                                <option value="All">All Categories ({categories.length})</option>
                                {categories.map(c => (
                                    <option key={c.id || c._id || c.name} value={c.name}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>

                            {/* Sort Selector */}
                            <select
                                className="ms-filter-select"
                                value={sortBy}
                                onChange={(e) => handleSortChange(e.target.value)}
                            >
                                <option value="distance">Nearest First</option>
                                <option value="relevance">Most Relevant</option>
                                <option value="rating">Highest Rated</option>
                                <option value="newest">Newest</option>
                                {activeMode === 'products' && (
                                    <>
                                        <option value="price_low">Price: Low to High</option>
                                        <option value="price_high">Price: High to Low</option>
                                    </>
                                )}
                            </select>

                            {/* Radius Chips Rail */}
                            <div className="ms-radius-chips">
                                {['auto', 5, 10, 15, 20, 30, 'all'].map(r => (
                                    <button
                                        key={r}
                                        type="button"
                                        className={`ms-radius-btn ${selectedRadius === r ? 'active' : ''}`}
                                        onClick={() => handleRadiusChange(r)}
                                    >
                                        {r === 'auto' ? '⚡ Dynamic' : (r === 'all' ? 'All' : `${r} km`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Subcategories Horizontal Bar */}
                    {selectedCategory !== 'All' && subcategoryList.length > 0 && (
                        <div className="ms-subcat-bar">
                            <span className="ms-subcat-label">Subcategory:</span>
                            <button
                                type="button"
                                className={`ms-subcat-chip ${selectedSubcategory === 'All' ? 'active' : ''}`}
                                onClick={() => handleSubcategorySelect('All')}
                            >
                                All in {selectedCategory.split('&')[0]}
                            </button>
                            {subcategoryList.map(sub => (
                                <button
                                    key={sub._id || sub.name}
                                    type="button"
                                    className={`ms-subcat-chip ${selectedSubcategory === sub.name ? 'active' : ''}`}
                                    onClick={() => handleSubcategorySelect(sub.name)}
                                >
                                    {sub.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ═════════════════ RESULTS BODY ═════════════════ */}
            <div className="ms-body">
                {/* Active Category Banner */}
                {selectedCategory !== 'All' && activeCatObj && (
                    <div className="ms-cat-banner">
                        <div className="ms-cat-banner-left">
                            <div className="ms-cat-banner-icon">
                                {getCategoryIcon(activeCatObj, 24)}
                            </div>
                            <div className="ms-cat-banner-text">
                                <h1>
                                    {activeCatObj.name}
                                    {selectedSubcategory !== 'All' && <span> &rsaquo; {selectedSubcategory}</span>}
                                </h1>
                                <p>
                                    {activeCatObj.desc || activeCatObj.tagline || 'Showing verified neighborhood shops and products.'}
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="ms-cat-clear-btn"
                            onClick={() => handleCategorySelect('All')}
                        >
                            <CloseIcon fontSize="inherit" />
                            <span>Clear Category</span>
                        </button>
                    </div>
                )}

                {/* Auto Expansion Notice */}
                {autoExpanded && (
                    <div className="ms-notice-banner">
                        <span>💡</span>
                        <span>
                            Limited matches within {selectedRadius} km. Results have been automatically expanded to <strong>{activeRadius === 'all' ? 'All Locations' : `${activeRadius} km`}</strong>.
                        </span>
                    </div>
                )}

                {/* Results Header Status Bar */}
                <div className="ms-results-info-bar">
                    <div className="ms-results-heading-col">
                        <h2>
                            {activeMode === 'shops'
                                ? `${totalShops} Local ${totalShops === 1 ? 'Shop' : 'Shops'} near ${location.label || 'Your Area'}`
                                : `${totalProducts} ${totalProducts === 1 ? 'Product' : 'Products'} Found`}
                        </h2>
                        <p>
                            Within {selectedRadius === 'all' ? 'all distances' : `${selectedRadius} km`} • Verified local sellers & direct in-store pickup
                        </p>
                    </div>

                    {/* Active Filter Chips */}
                    {isFiltered && (
                        <div className="ms-active-filters-row">
                            {selectedCategory !== 'All' && (
                                <span className="ms-filter-tag">
                                    {selectedCategory}
                                    <button onClick={() => handleCategorySelect('All')}><CloseIcon fontSize="inherit" /></button>
                                </span>
                            )}
                            {selectedSubcategory !== 'All' && (
                                <span className="ms-filter-tag">
                                    {selectedSubcategory}
                                    <button onClick={() => handleSubcategorySelect('All')}><CloseIcon fontSize="inherit" /></button>
                                </span>
                            )}
                            {selectedRadius !== 10 && (
                                <span className="ms-filter-tag">
                                    {selectedRadius === 'all' ? 'All distances' : `${selectedRadius} km`}
                                    <button onClick={() => handleRadiusChange(10)}><CloseIcon fontSize="inherit" /></button>
                                </span>
                            )}
                            <button
                                type="button"
                                className="ms-reset-filters-btn"
                                onClick={handleClearAllFilters}
                            >
                                Reset All
                            </button>
                        </div>
                    )}
                </div>

                {/* Main Results Container */}
                {loading ? (
                    <Loader />
                ) : (
                    <div>
                        {activeMode === 'shops' ? (
                            <div>
                                {shops.length > 0 ? (
                                    <div className="ms-shops-grid">
                                        {shops.map(shop => (
                                            <ShopCard key={shop._id} shop={shop} />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="ms-empty-card">
                                        <div className="ms-empty-icon-box">
                                            <StorefrontIcon />
                                        </div>
                                        <h3>
                                            {searchTerm
                                                ? `No shops found for "${searchTerm}"`
                                                : "No Shops Found in this Area"}
                                        </h3>
                                        <p>
                                            {totalProducts > 0
                                                ? `We found ${totalProducts} products from other stores. Or try expanding your search radius to explore shops in wider areas.`
                                                : "Try searching for another neighborhood, expanding radius, or browsing all departments."}
                                        </p>

                                        {totalProducts > 0 && (
                                            <button
                                                type="button"
                                                className="ms-empty-switch-btn"
                                                onClick={() => handleModeChange('products')}
                                            >
                                                <Inventory2Icon />
                                                <span>View {totalProducts} Products</span>
                                            </button>
                                        )}

                                        {selectedRadius !== 'all' && (
                                            <div>
                                                <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "700", display: "block", marginBottom: "8px" }}>
                                                    Expand search area:
                                                </span>
                                                <div className="ms-empty-radius-chips">
                                                    {['auto', 10, 15, 20, 30, 'all'].map(r => (
                                                        <button
                                                            key={r}
                                                            type="button"
                                                            className="ms-empty-radius-chip"
                                                            onClick={() => handleRadiusChange(r)}
                                                        >
                                                            {r === 'auto' ? '⚡ Dynamic' : (r === 'all' ? 'All Locations' : `${r} km`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Products Mode */
                            <div className="ms-products-layout">
                                {/* Facets Sidebar */}
                                <aside className="ms-facets-aside">
                                    {/* Price Range */}
                                    <div className="ms-facet-box">
                                        <h3>Price Range</h3>
                                        <div className="ms-facet-options">
                                            {PRICE_RANGES.map((pr, idx) => (
                                                <label key={pr.label} className="ms-facet-label">
                                                    <input
                                                        type="radio"
                                                        name="priceRange"
                                                        checked={selectedPriceIdx === idx}
                                                        onChange={() => handlePriceChange(idx)}
                                                    />
                                                    <span>{pr.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Minimum Rating */}
                                    <div className="ms-facet-box">
                                        <h3>Minimum Rating</h3>
                                        <div className="ms-facet-options">
                                            {[0, 4.0, 4.5].map((rt) => (
                                                <label key={rt} className="ms-facet-label">
                                                    <input
                                                        type="radio"
                                                        name="ratingFilter"
                                                        checked={minRating === rt}
                                                        onChange={() => handleRatingChange(rt)}
                                                    />
                                                    <span>{rt === 0 ? 'All Ratings' : `${rt}★ & Above`}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Brands */}
                                    {availableBrands.length > 0 && (
                                        <div className="ms-facet-box">
                                            <h3>Brand</h3>
                                            <div className="ms-facet-options" style={{ maxHeight: "180px", overflowY: "auto" }}>
                                                <label className="ms-facet-label">
                                                    <input
                                                        type="radio"
                                                        name="brandFilter"
                                                        checked={selectedBrand === 'All'}
                                                        onChange={() => handleBrandChange('All')}
                                                    />
                                                    <span>All Brands</span>
                                                </label>
                                                {availableBrands.slice(0, 10).map(b => (
                                                    <label key={b} className="ms-facet-label">
                                                        <input
                                                            type="radio"
                                                            name="brandFilter"
                                                            checked={selectedBrand === b}
                                                            onChange={() => handleBrandChange(b)}
                                                        />
                                                        <span>{b}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {isFiltered && (
                                        <button
                                            type="button"
                                            className="ms-cat-clear-btn"
                                            style={{ width: "100%", justifyContent: "center" }}
                                            onClick={handleClearAllFilters}
                                        >
                                            Reset All Filters
                                        </button>
                                    )}
                                </aside>

                                {/* Products Grid */}
                                <div>
                                    {products.length > 0 ? (
                                        <div className="ms-products-grid">
                                            {products.map(prod => (
                                                <MarketplaceProductCard key={prod._id} product={prod} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="ms-empty-card">
                                            <div className="ms-empty-icon-box">
                                                <Inventory2Icon />
                                            </div>
                                            <h3>No Products Found</h3>
                                            <p>
                                                {totalShops > 0
                                                    ? `We found ${totalShops} local shops that might stock this item.`
                                                    : "Try broadening your filters or searching another category."}
                                            </p>
                                            {totalShops > 0 && (
                                                <button
                                                    type="button"
                                                    className="ms-empty-switch-btn"
                                                    onClick={() => handleModeChange('shops')}
                                                >
                                                    <StorefrontIcon />
                                                    <span>View {totalShops} Local Shops</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Global Footer */}
            <Footer />
        </div>
    );
}

export default MarketplaceSearch;
